import React, { useState, useRef, useEffect } from 'react'
import {
  ChevronDown,
  ClipboardList,
  PlusCircle,
  ShieldCheck,
  LogOut,
  Sun,
  Moon,
  Monitor,
  User as UserIcon,
} from 'lucide-react'
import type { User } from 'firebase/auth'
import type { AppUser } from '../types/poptavky'
import { useTheme, type ThemeMode } from '../context/ThemeContext'

export interface UserProfileMenuProps {
  googleUser: User
  appUser: AppUser | null
  myInquiriesCount: number
  isCurrentUserAdmin: boolean
  pendingAdminCount?: number
  activeView: string
  onNavigate: (path: string) => void
  onSignOut: () => void
}

export const UserProfileMenu: React.FC<UserProfileMenuProps> = ({
  googleUser,
  appUser,
  myInquiriesCount,
  isCurrentUserAdmin,
  pendingAdminCount = 0,
  activeView,
  onNavigate,
  onSignOut,
}) => {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const { themeMode, setThemeMode } = useTheme()

  const displayName = appUser?.displayName || googleUser.displayName || 'Můj účet'
  const email = appUser?.email || googleUser.email || ''
  const photoURL = googleUser.photoURL || appUser?.photoURL

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectNav = (path: string) => {
    setOpen(false)
    onNavigate(path)
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`inline-flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-2xl border transition-all cursor-pointer ${
          open
            ? 'bg-brand-50 dark:bg-slate-800 border-brand-500 ring-2 ring-brand-500/20'
            : 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/80 border-slate-200 dark:border-slate-700 shadow-2xs'
        }`}
      >
        {photoURL ? (
          <img
            src={photoURL}
            alt={displayName}
            className="w-7 h-7 rounded-xl object-cover shrink-0"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-7 h-7 rounded-xl bg-brand-600 text-white text-xs font-extrabold flex items-center justify-center shrink-0">
            {(displayName || 'U')[0].toUpperCase()}
          </div>
        )}

        <div className="text-left hidden sm:block max-w-[140px]">
          <div className="text-xs font-extrabold text-slate-900 dark:text-white truncate leading-tight">
            {displayName}
          </div>
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 truncate leading-tight">
            {isCurrentUserAdmin ? 'Administrátor' : `Moje poptávky (${myInquiriesCount})`}
          </div>
        </div>

        {myInquiriesCount > 0 && (
          <span className="sm:hidden px-1.5 py-0.5 rounded-full bg-brand-600 text-white text-[10px] font-extrabold">
            {myInquiriesCount}
          </span>
        )}

        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${
            open ? 'rotate-180 text-brand-600 dark:text-brand-400' : ''
          }`}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Hlavička účtu */}
          <div className="p-4 bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3">
            {photoURL ? (
              <img
                src={photoURL}
                alt={displayName}
                className="w-10 h-10 rounded-xl object-cover shrink-0"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-brand-600 text-white text-sm font-extrabold flex items-center justify-center shrink-0">
                <UserIcon className="w-5 h-5" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-extrabold text-sm text-slate-900 dark:text-white truncate">
                {displayName}
              </div>
              {email && (
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{email}</div>
              )}
              <div className="mt-1">
                {isCurrentUserAdmin ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold">
                    <ShieldCheck className="w-3 h-3" />
                    Administrátor systému
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-brand-50 dark:bg-brand-950/70 text-brand-700 dark:text-brand-300 text-[10px] font-bold">
                    Ověřený účet klienta
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Položky menu: Moje poptávky, Nová poptávka, Admin */}
          <div className="p-2 space-y-1">
            <button
              type="button"
              onClick={() => handleSelectNav('/moje-poptavky')}
              className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-colors cursor-pointer ${
                activeView === 'client_dashboard'
                  ? 'bg-brand-600 text-white'
                  : 'text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <span className="flex items-center gap-2.5">
                <ClipboardList className="w-4 h-4 shrink-0" />
                <span>Moje poptávky a nabídky</span>
              </span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-extrabold ${
                  activeView === 'client_dashboard'
                    ? 'bg-white/20 text-white'
                    : 'bg-brand-100 dark:bg-brand-950 text-brand-700 dark:text-brand-300'
                }`}
              >
                {myInquiriesCount}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleSelectNav('/zadat-poptavku')}
              className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-colors cursor-pointer ${
                activeView === 'new_inquiry'
                  ? 'bg-brand-600 text-white'
                  : 'text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <span className="flex items-center gap-2.5">
                <PlusCircle className="w-4 h-4 shrink-0" />
                <span>Zadat novou poptávku</span>
              </span>
            </button>

            {isCurrentUserAdmin && (
              <button
                type="button"
                onClick={() => handleSelectNav('/admin')}
                className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-colors cursor-pointer ${
                  activeView === 'admin_portal'
                    ? 'bg-emerald-600 text-white'
                    : 'text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/50'
                }`}
              >
                <span className="flex items-center gap-2.5">
                  <ShieldCheck className="w-4 h-4 shrink-0" />
                  <span>Administrace systému</span>
                </span>
                {pendingAdminCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-[11px] font-extrabold">
                    {pendingAdminCount} ke schválení
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Přepínač vzhledu (Světlý / Tmavý / Auto) přímo v menu */}
          <div className="px-3.5 py-3 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
            <div className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
              Režim zobrazení
            </div>
            <div className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800">
              {[
                { id: 'light' as ThemeMode, label: 'Světlý', icon: <Sun className="w-3.5 h-3.5" /> },
                { id: 'dark' as ThemeMode, label: 'Tmavý', icon: <Moon className="w-3.5 h-3.5" /> },
                { id: 'system' as ThemeMode, label: 'Auto', icon: <Monitor className="w-3.5 h-3.5" /> },
              ].map((m) => {
                const active = themeMode === m.id
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setThemeMode(m.id)}
                    className={`flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      active
                        ? 'bg-white dark:bg-slate-900 text-brand-600 dark:text-brand-400 shadow-2xs'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    {m.icon}
                    <span>{m.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Odhlášení dole v profilovém menu */}
          <div className="p-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                onSignOut()
              }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 transition-colors cursor-pointer"
            >
              <LogOut className="w-4 h-4 shrink-0" />
              <span>Odhlásit se z účtu</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserProfileMenu
